import { match } from 'path-to-regexp';
import { hasValue, isUserAllowed } from "@utils";
/**
 * A navigation link URL is a PATH plus an optional query string. The path identifies the
 * route; the query is a customisation of the link (it opens the page with those defaults and
 * reaches the component as props). Everything that matches a link against the current URL
 * matches on the path and then, separately, on the query pairs.
 */
export function splitLink(link) {
    const raw = link !== null && link !== void 0 ? link : '';
    const i = raw.indexOf('?');
    if (i < 0)
        return { path: raw, query: {} };
    const query = {};
    new URLSearchParams(raw.slice(i + 1)).forEach((value, key) => { query[key] = value; });
    return { path: raw.slice(0, i), query };
}
/**
 * Every pair the link carries is present in the current browser query. Keys and values are
 * compared case-sensitively (a query value is data, unlike a path).
 */
function queryMatchesCurrent(query) {
    const keys = Object.keys(query);
    if (!keys.length)
        return true;
    const current = new URLSearchParams(window.location.search);
    return keys.every(key => current.get(key) === query[key]);
}
/**
 * Another link sits on the same path, carries a query, and that query matches the current URL —
 * so it, not this query-less link, is the one to highlight. `allLinks` is whatever set of links
 * the caller is rendering; with no set to compare against, a path match stands on its own.
 */
function hasBetterQueryMatch(linkPath, link, allLinks) {
    if (!(allLinks === null || allLinks === void 0 ? void 0 : allLinks.length))
        return false;
    const normalized = normalizePath(linkPath);
    return allLinks.some(other => {
        if (!other || other === link)
            return false;
        if (hasValue(other.id) && other.id === (link === null || link === void 0 ? void 0 : link.id))
            return false;
        const { path, query } = splitLink(other.link);
        if (!Object.keys(query).length)
            return false;
        if (normalizePath(path) !== normalized)
            return false;
        return queryMatchesCurrent(query);
    });
}
/** The link's own URL (path + query) points at the page currently on screen. */
function matchesLinkUrl(link, allLinks) {
    const { path, query } = splitLink(link === null || link === void 0 ? void 0 : link.link);
    if (!isExactPathMatch(path) && !matchesDynamicRouteBase(path))
        return false;
    // A link with parameters is active only when all of them are in the current query.
    if (Object.keys(query).length)
        return queryMatchesCurrent(query);
    // A link without parameters yields to a link on the same path whose parameters do match.
    return !hasBetterQueryMatch(path, link, allLinks);
}
// Helper functions
function isExactPathMatch(linkPath) {
    const currentPath = getNormalizedCurrentPath();
    const normalizedLink = normalizePath(splitLink(linkPath).path);
    return currentPath === normalizedLink;
}
function matchesDynamicRouteBase(linkPath) {
    const currentPath = getNormalizedCurrentPath();
    const normalizedLink = normalizePath(splitLink(linkPath).path);
    // Handle dynamic routes like /view/location/details/:key
    if (normalizedLink === null || normalizedLink === void 0 ? void 0 : normalizedLink.includes('/:')) {
        const basePath = normalizedLink.split('/:')[0];
        return currentPath.startsWith(basePath + '/');
    }
    // Handle cases like /view/location matching /view/location/details/123
    return currentPath.startsWith(normalizedLink + '/');
}
function getNormalizedCurrentPath() {
    const { pathname } = window.location;
    return normalizePath(pathname);
}
function normalizePath(path) {
    var _a, _b;
    return (_b = (_a = path === null || path === void 0 ? void 0 : path.replace(/^\s+|\s+$/g, '') // Trim whitespace
    ) === null || _a === void 0 ? void 0 : _a.replace(/^\/+|\/+$/g, '') // Trim leading/trailing slashes
    ) === null || _b === void 0 ? void 0 : _b.toLowerCase(); // Convert to lowercase
}
/**
 * @param allLinks the links rendered alongside this one — lets two links on the same path be
 * told apart by their query strings. Optional: without it a path match stands on its own.
 */
export function isActiveLink(link, selectedItem, ignoreSelectedChildren, allLinks) {
    var _a;
    if (!!selectedItem)
        return ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) === link.id);
    if (matchesLinkUrl(link, allLinks))
        return true;
    if (((_a = link.children) === null || _a === void 0 ? void 0 : _a.length) && !ignoreSelectedChildren) {
        const siblings = (allLinks === null || allLinks === void 0 ? void 0 : allLinks.length) ? allLinks : link.children;
        return link.children.some(child => {
            var _a;
            return matchesLinkUrl(child, siblings) ||
                ((_a = child.children) === null || _a === void 0 ? void 0 : _a.some(cc => isActiveLink(cc, undefined, false, siblings)));
        });
    }
    return false;
}
export function processNavigationLinks(context, isAnonymous) {
    // assume sidebar items are formatted NavigationLink format
    // doing a type cast
    // need to handle items registered through code
    // const items = _.cloneDeep(((env == 'dev') ? getSidebarItemsInDev() : context?.navigationLinks || []) as unknown[]) as NavigationLink[];
    const items = (context === null || context === void 0 ? void 0 : context.navigationLinks) || [];
    return splitNavigationLinks(context, items, isAnonymous);
}
export function splitNavigationLinks(context, links, isAnonymous) {
    const top = {};
    const filterAllowed = (items) => {
        // Backend now returns only the links this user is allowed to see (profile-filtered for
        // authenticated users, public-only for anonymous). No client-side access checks needed.
        return items.map(item => (Object.assign(Object.assign({}, item), { children: item.children ? filterAllowed(item.children) : [] })));
    };
    const allowedLinks = filterAllowed(links);
    const resolveFirstLeafLink = (items) => {
        for (const item of items) {
            if (hasValue(item.link))
                return item.link;
            const nested = resolveFirstLeafLink(item.children || []);
            if (nested)
                return nested;
        }
        return null;
    };
    const resolveGroupLinks = (items) => {
        return items.map(item => {
            var _a, _b;
            const processedChildren = ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length)
                ? resolveGroupLinks(item.children)
                : item.children;
            if (!hasValue(item.link) && (processedChildren === null || processedChildren === void 0 ? void 0 : processedChildren.length)) {
                return Object.assign(Object.assign({}, item), { children: processedChildren, link: (_b = resolveFirstLeafLink(processedChildren)) !== null && _b !== void 0 ? _b : item.link });
            }
            return Object.assign(Object.assign({}, item), { children: processedChildren });
        });
    };
    const linksToProcess = isAnonymous ? resolveGroupLinks(allowedLinks) : allowedLinks;
    const flatten = flattenSidebarLinks(linksToProcess);
    const getParent = (item) => {
        return flatten.find(l => l.id === item.parentId) || null;
    };
    const process = (items, level, parentId = null) => {
        return items.map(item => {
            const { children } = item;
            const newItem = Object.assign(Object.assign({}, item), { parentId });
            if (children === null || children === void 0 ? void 0 : children.length) {
                if (level < 3) {
                    newItem.children = process(children, level + 1, item.id);
                }
                else if (level === 3) {
                    top[item.id] = {
                        parent: getParent(newItem),
                        children: children.map(child => (Object.assign(Object.assign({}, child), { parentId: item.id }))),
                        link: newItem
                    };
                }
            }
            return newItem;
        });
    };
    const sidebar = process(linksToProcess, 1);
    return { sidebar, top, all: links };
}
export function isGroup(link) {
    var _a;
    return (link === null || link === void 0 ? void 0 : link.type) === 'group' || (!!(link === null || link === void 0 ? void 0 : link.children) && ((_a = link.children) === null || _a === void 0 ? void 0 : _a.length) > 0);
}
export function hasGroupedLinks(items) {
    return items === null || items === void 0 ? void 0 : items.some(l => isGroup(l));
}
export function flattenSidebarLinks(links) {
    const flatten = [];
    const collect = (items) => {
        var _a;
        for (const item of items) {
            flatten.push(Object.assign({}, item));
            if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
                collect(item.children);
            }
        }
    };
    if (links === null || links === void 0 ? void 0 : links.length) {
        collect(links);
    }
    return flatten;
}
export function getActiveTopNavigationLink(topNavigation) {
    // The whole top-navigation set is the comparison universe, so a link carrying query
    // parameters wins over a query-less link on the same path (see isActiveLink).
    const universe = [];
    Object.keys(topNavigation).forEach(linkId => {
        var _a;
        const entry = topNavigation[linkId];
        if (entry === null || entry === void 0 ? void 0 : entry.link)
            universe.push(entry.link);
        if ((_a = entry === null || entry === void 0 ? void 0 : entry.children) === null || _a === void 0 ? void 0 : _a.length)
            universe.push(...entry.children);
    });
    const activeLink = Object.keys(topNavigation).find(linkId => {
        return isActiveLink(topNavigation[linkId].link, undefined, false, universe);
    });
    if (hasValue(activeLink))
        return (topNavigation === null || topNavigation === void 0 ? void 0 : topNavigation[activeLink]) || null;
    return null;
}
const csvList = (v) => String(v !== null && v !== void 0 ? v : '').split(',').map(s => s.trim()).filter(Boolean);
// New format (NavLinksEditor): camelCase fields, tree via parentID. Old v4 format (PortalLink):
// PascalCase fields, nested Children, a Configuration object and Roles.
const isNewFormatDashboardLink = (item) => !(item === null || item === void 0 ? void 0 : item.Configuration) && !(item === null || item === void 0 ? void 0 : item.ID) && hasValue(item === null || item === void 0 ? void 0 : item.id);
/** The access level a dashboard link declares itself, or null when it declares none. */
function ownDashboardLinkLevel(item) {
    var _a;
    const level = isNewFormatDashboardLink(item)
        ? { userGroups: csvList((_a = item.userGroups) !== null && _a !== void 0 ? _a : item.roles), appRoles: csvList(item.appRoles) }
        : { userGroups: csvList(item.Roles), appRoles: [] };
    return level.userGroups.length || level.appRoles.length ? level : null;
}
/**
 * Flattens a dashboard's links (both stored formats) into entries with their section path and the
 * restricted levels on their branch. A restriction on a parent applies to its children, as it
 * does for master navigation on the server.
 */
export function getDashboardLinkEntries(details) {
    const items = ((details === null || details === void 0 ? void 0 : details.ParsedSidebarItems) || []);
    const base = `/dashboard/${details === null || details === void 0 ? void 0 : details.ID}`;
    const entries = [];
    const add = (item, parentLevels) => {
        const own = ownDashboardLinkLevel(item);
        const levels = own ? [...parentLevels, own] : parentLevels;
        let path;
        if (isNewFormatDashboardLink(item)) {
            path = item.type === 'group' || item.type === 'externalLink' ? null : (splitLink(item.link).path || null);
        }
        else {
            const cfg = item.Configuration || {};
            path = cfg.isGrouped ? null : hasValue(cfg.dashboardKey) ? `${base}/${cfg.dashboardKey}` : (splitLink(cfg.link).path || null);
        }
        if (path && !path.startsWith('/dashboard/'))
            path = null;
        entries.push({ item, type: isNewFormatDashboardLink(item) ? item.type : undefined, path, levels });
        return levels;
    };
    if (items.length && isNewFormatDashboardLink(items[0])) {
        const byId = {};
        items.forEach(i => { byId[i.id] = i; });
        const levelsOf = (item, seen = new Set()) => {
            const parent = item.parentID && !seen.has(item.parentID) ? byId[item.parentID] : null;
            if (!parent)
                return [];
            seen.add(item.parentID);
            const parentLevels = levelsOf(parent, seen);
            const own = ownDashboardLinkLevel(parent);
            return own ? [...parentLevels, own] : parentLevels;
        };
        items.forEach(i => add(i, levelsOf(i)));
    }
    else {
        const walk = (list, parentLevels) => (list || []).forEach(i => walk(i.Children, add(i, parentLevels)));
        walk(items, []);
    }
    return entries;
}
/** Every restricted level on the link's branch passes. A session without a user never passes a restricted one. */
export function isDashboardLinkAllowed(context, entry) {
    return entry.levels.every(l => !(context === null || context === void 0 ? void 0 : context.isPublicSession) && isUserAllowed(context, l.userGroups, l.appRoles));
}
/** The dashboard link whose section path matches `pathname` (most specific first), with the matched params. */
export function findDashboardLink(details, pathname) {
    const specificity = (p) => {
        const segs = p.split('/').filter(Boolean);
        return segs.length * 100 - segs.filter(s => s.startsWith(':')).length;
    };
    const candidates = getDashboardLinkEntries(details)
        .filter(e => !!e.path)
        .sort((a, b) => specificity(b.path) - specificity(a.path));
    for (const entry of candidates) {
        let matched;
        try {
            matched = match(entry.path, { decode: decodeURIComponent })(pathname);
        }
        catch (_a) {
            continue;
        } // a pattern path-to-regexp can't parse matches nothing
        if (matched)
            return { entry, params: matched.params || {} };
    }
    return null;
}
/**
 * Converts dashboard sidebar items (PortalLink[]) to TopNavigationLinks format
 * For dashboards, the dashboard title acts as the parent with sidebar items as children
 * @param dashboardConfig - The dashboard's details (title, id, parsed sidebar items)
 * @param context - When given, links the user may not see (User Groups / App Roles on the link or a
 *                  parent) are left out, and a group left without children is dropped
 * @returns TopNavigationLinks structure for rendering
 */
export function convertDashboardSidebarToTopNavigation(dashboardConfig, context) {
    const { details: { Title, ID, ParsedSidebarItems } } = dashboardConfig;
    // Convert PortalLink (old format) or DashboardNavLink (new format) to NavigationLink.
    // Old format uses PascalCase fields + a nested Configuration object.
    // New format (from NavLinksEditor) uses camelCase fields directly on the item.
    const convertToNavigationLink = (item, parentLink, parentLevels = []) => {
        var _a, _b, _c;
        const { ID, Roles, Label, Icon, Configuration } = item;
        // New format detection: has lowercase 'id' but no 'Configuration' or 'ID'
        const isNewFormat = !Configuration && !ID && hasValue(item.id);
        const own = ownDashboardLinkLevel(item);
        const levels = own ? [...parentLevels, own] : parentLevels;
        if (context && !isDashboardLinkAllowed(context, { levels }))
            return null;
        const resolvedId = isNewFormat ? item.id : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.id) || ID || '');
        const resolvedLabel = isNewFormat ? item.label : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.label) || Label || '');
        const resolvedIcon = isNewFormat ? (item.icon || '') : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.icon) || Icon || '');
        const resolvedRoles = isNewFormat ? ((_b = (_a = item.userGroups) !== null && _a !== void 0 ? _a : item.roles) !== null && _b !== void 0 ? _b : '') : (Roles || '');
        let link;
        if (isNewFormat) {
            link = item.type === 'group' ? null : (item.link || '#');
        }
        else {
            link = (Configuration === null || Configuration === void 0 ? void 0 : Configuration.isGrouped)
                ? null
                : hasValue(Configuration === null || Configuration === void 0 ? void 0 : Configuration.dashboardKey)
                    ? `${parentLink}/${Configuration.dashboardKey}`
                    : (Configuration === null || Configuration === void 0 ? void 0 : Configuration.link) || '#';
        }
        const children = item.Children && item.Children.length > 0
            ? item.Children
                .map((child) => convertToNavigationLink(child, parentLink, levels))
                .filter(Boolean)
            : [];
        // A group whose children were all hidden has nothing left to open.
        if (context && link === null && ((_c = item.Children) === null || _c === void 0 ? void 0 : _c.length) > 0 && children.length === 0)
            return null;
        const target = isNewFormat
            ? (item.type === 'externalLink' ? '_blank' : undefined)
            : ((Configuration === null || Configuration === void 0 ? void 0 : Configuration.target) || undefined);
        return Object.assign({ id: resolvedId, label: resolvedLabel, icon: resolvedIcon, link: link !== null && link !== void 0 ? link : '#', type: isNewFormat ? (item.type || undefined) : undefined, userGroups: csvList(resolvedRoles), appRoles: isNewFormat ? csvList(item.appRoles) : [], children }, (target ? { target } : {}));
    };
    // Create parent navigation link representing the dashboard
    const parentLink = {
        id: `dashboard-${ID}`,
        label: Title,
        icon: 'dashboard',
        link: `/dashboard/${ID}`,
        userGroups: [],
        appRoles: []
    };
    // Convert all sidebar items to NavigationLink format.
    // Old format: flat list of items with nested Children arrays.
    // New format (NavLinksEditor): flat list with parentID — build tree first.
    let topLevelItems = [];
    if (ParsedSidebarItems && ParsedSidebarItems.length > 0) {
        const firstItem = ParsedSidebarItems[0];
        const isNewFormat = !firstItem.Configuration && !firstItem.ID && hasValue(firstItem.id);
        if (isNewFormat) {
            // Build tree from flat list using parentID
            const sorted = [...ParsedSidebarItems].sort((a, b) => (a.priority || 0) - (b.priority || 0));
            const byId = {};
            sorted.forEach(item => { byId[item.id] = Object.assign(Object.assign({}, item), { Children: [] }); });
            sorted.forEach(item => {
                if (item.parentID && byId[item.parentID]) {
                    byId[item.parentID].Children.push(byId[item.id]);
                }
            });
            topLevelItems = sorted.filter(item => !item.parentID).map(item => byId[item.id]);
        }
        else {
            topLevelItems = ParsedSidebarItems;
        }
    }
    const children = topLevelItems.length > 0
        ? topLevelItems.map(item => convertToNavigationLink(item, parentLink.link)).filter(Boolean)
        : [];
    // Create TopNavigationLinks structure
    const topNavigation = {
        [parentLink.id]: {
            parent: null, // No parent for dashboard items
            children: children,
            link: parentLink
        }
    };
    return topNavigation;
}
/**
 * One tree for the mobile menu: the sidebar tree (levels 1-4) plus dashboard sub-pages, which
 * live only in the top navigation, under the dashboard link they belong to.
 */
export function buildMenuTree(groups) {
    const subPagesByLink = {};
    Object.values((groups === null || groups === void 0 ? void 0 : groups.top) || {}).forEach(entry => {
        var _a, _b;
        if (!(entry === null || entry === void 0 ? void 0 : entry.parent) && ((_a = entry === null || entry === void 0 ? void 0 : entry.link) === null || _a === void 0 ? void 0 : _a.link) && ((_b = entry.children) === null || _b === void 0 ? void 0 : _b.length))
            subPagesByLink[entry.link.link] = entry.children;
    });
    const attach = (items) => (items || []).map(item => {
        var _a;
        const children = ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) ? attach(item.children) : subPagesByLink[item.link];
        return (children === null || children === void 0 ? void 0 : children.length) ? Object.assign(Object.assign({}, item), { children }) : item;
    });
    return attach((groups === null || groups === void 0 ? void 0 : groups.sidebar) || []);
}
/**
 * The path from the top of the menu tree to the link of the current page (deepest match
 * first), or an empty array when no link matches.
 */
export function findActiveMenuPath(tree) {
    const all = flattenSidebarLinks(tree);
    const walk = (items, path) => {
        var _a;
        for (const item of items) {
            if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
                const found = walk(item.children, [...path, item]);
                if (found)
                    return found;
            }
            if (hasValue(item.link) && isActiveLink(item, undefined, true, all))
                return [...path, item];
        }
        return null;
    };
    return walk(tree || [], []) || [];
}
